import { sharedIntegrationsAvailable } from '@mygpt/app/config/integrations';
import { Checkbox, ColorInput, Group, Image, Radio, Slider, Stack, Switch, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import { BetaBadge } from '@/components/Common/BetaBadge';
import { CanAccess } from '@mygpt/app';
import DisplayMethod from '@/components/Widget/DisplayMethod';

interface Props {
  form: UseFormReturnType<any>;
  onOverrideChangeForForm: (prop: string) => (data?: any) => void;
}

const MESSAGE_BACKGROUND_OPACITY_FIELD = 'digitalHuman.messageBackgroundOpacity';
const DEFAULT_MESSAGE_BACKGROUND_OPACITY = 50;
/** 未开启自定义背景色时，预览区使用的兜底底色 */
const PREVIEW_FALLBACK_BACKGROUND = '#C5C9EE';

const clampOpacity = (value: number) => Math.min(100, Math.max(0, Math.round(value)));

const DigitalHumanSettings = ({ form, onOverrideChangeForForm }: Props) => {
  const { t } = useTranslation();
  const configuredMessageBackgroundOpacity = form.values.digitalHuman?.messageBackgroundOpacity;
  const messageBackgroundOpacity = Number.isFinite(configuredMessageBackgroundOpacity)
    ? clampOpacity(configuredMessageBackgroundOpacity)
    : DEFAULT_MESSAGE_BACKGROUND_OPACITY;
  const messageBackgroundAlpha = messageBackgroundOpacity / 100;
  const previewBackgroundColor =
    form.values.digitalHuman?.backgroundColor?.checked && form.values.digitalHuman.backgroundColor.value
      ? form.values.digitalHuman.backgroundColor.value
      : PREVIEW_FALLBACK_BACKGROUND;

  // 拖动中只更新表单，松手（onChangeEnd）才触发保存
  const commitMessageBackgroundOpacity = onOverrideChangeForForm(MESSAGE_BACKGROUND_OPACITY_FIELD);

  const digitalHumanOptions = useMemo(() => {
    const options: Array<{
      value: string;
      label: string;
      image: string;
      beta: boolean;
    }> = [
      {
        value: 'sparticle',
        label: t('widget.setting.digital_human.sparticle'),
        image: '/assets/digital-human/sparticle.jpg',
        beta: true,
      },
      {
        value: 'normal',
        label: t('widget.setting.digital_human.normal'),
        image: '/assets/digital-human/normal.jpg',
        beta: false,
      },
    ];

    // 检查是否有textureUrl，如果有则添加custom选项
    const hasTextureUrl =
      sharedIntegrationsAvailable &&
      form.values.digitalHuman?.textureUrl &&
      form.values.digitalHuman.textureUrl.trim() !== '';
    if (hasTextureUrl) {
      // 从textureUrl中提取UUID
      const textureUrl = form.values.digitalHuman.textureUrl;
      const uuidMatch = textureUrl.match(/textures\/([a-f0-9-]+)\/texture\.png$/);
      const uuid = uuidMatch ? uuidMatch[1] : null;

      // 生成预览图URL
      const previewImage = uuid ? `https://avatar.gbase.ai/s3/avatar-textures/screenshots/${uuid}/screenshot.png` : '';

      options.push({
        value: 'custom',
        label: t('widget.setting.digital_human.custom'),
        image: previewImage,
        beta: true,
      });
    }

    return options;
  }, [t, JSON.stringify(form.values.digitalHuman)]);

  return (
    <Stack gap={16} className="select-none pt-5">
      {/* 2D数字人开关 */}
      <CanAccess accessKey="enable_2d_digital_human">
        <section className="bg-white rounded-md shadow-sm p-5">
          <Group gap={5} mb={8}>
            <Text size="sm">{t('settings.digital_human.label')}</Text>
            <BetaBadge />
          </Group>
          <div className="bg-gray-50 rounded-lg py-4 px-4">
            <Switch
              label={t('settings.digital_human.desc')}
              checked={form.values.enable_2d_digital_person}
              onChange={(event) => onOverrideChangeForForm('enable_2d_digital_person')(event.currentTarget.checked)}
            />
          </div>
        </section>
      </CanAccess>

      {/* 数字人设置 - 只有当开关打开时才显示 */}
      {form.values.enable_2d_digital_person && (
        <section className="bg-white rounded-md shadow-sm p-5">
          <Stack gap={24}>
            <div>
              <Title order={4}>{t('widget.setting.digital_human.title')}</Title>
              <Text size="sm" c="gray.6" mt={10}>
                {t('widget.setting.digital_human.desc')}
              </Text>
            </div>

            {/* 背景颜色设置 */}
            <Stack gap={12}>
              <Checkbox
                label={t('widget.setting.digital_human.background_color_label')}
                description={t('widget.setting.digital_human.background_color_desc')}
                checked={form.values.digitalHuman.backgroundColor.checked}
                onChange={(event) =>
                  onOverrideChangeForForm('digitalHuman.backgroundColor.checked')(event.currentTarget.checked)
                }
              />
              <div className="w-[150px] ml-8">
                <ColorInput
                  disabled={!form.values.digitalHuman.backgroundColor.checked}
                  size="xs"
                  autoComplete="off"
                  value={form.values.digitalHuman.backgroundColor.value}
                  onChange={(value) => onOverrideChangeForForm('digitalHuman.backgroundColor.value')(value)}
                />
              </div>
            </Stack>

            {/* 消息背景透明度 */}
            <Stack gap={12}>
              <Stack gap={4}>
                <Text size="sm" fw={600}>
                  {t('widget.setting.digital_human.message_background_opacity_label')}
                </Text>
                <Text size="xs" c="gray.6">
                  {t('widget.setting.digital_human.message_background_opacity_desc')}
                </Text>
              </Stack>
              <Stack gap={16} className="border border-gray-200 rounded-xl p-4 max-w-[480px]">
                {/* 实时预览：与小部件内消息气泡使用同一套样式 */}
                <div
                  className="flex min-h-[96px] items-center justify-center rounded-lg p-3"
                  style={{ backgroundColor: previewBackgroundColor }}
                >
                  <div
                    className="max-w-full rounded-xl px-3 py-2"
                    style={{
                      background: `linear-gradient(0deg, rgba(0, 0, 0, ${messageBackgroundAlpha}) 0%, rgba(84, 84, 84, ${messageBackgroundAlpha}) 100%)`,
                      backdropFilter: 'blur(12px)',
                      textShadow: '0 0 10px #000000',
                      letterSpacing: '0.05em',
                    }}
                  >
                    <Text size="xs" c="white">
                      {t('widget.setting.digital_human.message_background_opacity_preview')}
                    </Text>
                  </div>
                </div>

                <Group gap={12} wrap="nowrap" align="center">
                  <Slider
                    className="flex-1 min-w-0"
                    min={0}
                    max={100}
                    step={1}
                    value={messageBackgroundOpacity}
                    label={(value) => `${value}%`}
                    onChange={(value) => form.setFieldValue(MESSAGE_BACKGROUND_OPACITY_FIELD, value)}
                    onChangeEnd={commitMessageBackgroundOpacity}
                  />
                  <Text size="sm" fw={500} w={40} ta="right" className="tabular-nums shrink-0">
                    {messageBackgroundOpacity}%
                  </Text>
                </Group>
              </Stack>
            </Stack>

            {/* 选择数字人形象 */}
            <Stack gap={12}>
              <Text size="sm" fw={600}>
                {t('widget.setting.digital_human.image_select')}
              </Text>
              <Radio.Group
                name="digitalHumanImage"
                value={form.values.digitalHuman.image}
                onChange={(value) => onOverrideChangeForForm('digitalHuman.image')(value)}
              >
                <Group className="border border-gray-200 rounded-xl p-4 items-start" justify="space-around">
                  {digitalHumanOptions.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      label={
                        <Stack className="flex items-center gap-2 flex-shrink-0 w-[140px]">
                          <Group gap={5}>
                            <Text size="sm">{option.label}</Text>
                            {option.beta && <BetaBadge />}
                          </Group>
                          {option.image ? (
                            <Image
                              src={option.image}
                              alt={option.label}
                              h={200}
                              w="auto"
                              fit="contain"
                              className="flex-shrink-0"
                              onError={(e) => {
                                // 当screenshot不存在时，隐藏图片
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : null}
                        </Stack>
                      }
                    />
                  ))}
                </Group>
              </Radio.Group>
            </Stack>

            {/* 播放欢迎语设置 */}
            <Stack gap={12}>
              <Checkbox
                label={t('widget.setting.digital_human.play_welcome_message_label')}
                checked={form.values.digitalHuman.playWelcomeMessage.enabled}
                onChange={(event) =>
                  onOverrideChangeForForm('digitalHuman.playWelcomeMessage.enabled')(event.currentTarget.checked)
                }
              />
              <Radio.Group
                className="border rounded-lg p-3"
                name="digitalHumanPlayWelcomeMessageMode"
                value={form.values.digitalHuman.playWelcomeMessage.mode}
                onChange={(value) => onOverrideChangeForForm('digitalHuman.playWelcomeMessage.mode')(value)}
              >
                <Stack>
                  <Radio
                    size="xs"
                    value="first"
                    label={t('widget.setting.digital_human.play_welcome_message_first')}
                    description={t('widget.setting.digital_human.play_welcome_message_first_desc')}
                    disabled={!form.values.digitalHuman.playWelcomeMessage.enabled}
                  />
                  <Radio
                    size="xs"
                    value="every"
                    label={t('widget.setting.digital_human.play_welcome_message_every')}
                    description={t('widget.setting.digital_human.play_welcome_message_every_desc')}
                    disabled={!form.values.digitalHuman.playWelcomeMessage.enabled}
                  />
                </Stack>
              </Radio.Group>
            </Stack>

            {/* 默认输入模式设置 */}
            <Stack gap={12}>
              <Text size="sm" fw={600}>
                {t('widget.setting.digital_human.default_input_mode_label')}
              </Text>
              <Radio.Group
                className="border rounded-lg p-3"
                name="digitalHumanDefaultInputMode"
                value={form.values.digitalHuman.defaultInputMode}
                onChange={(value) => onOverrideChangeForForm('digitalHuman.defaultInputMode')(value)}
              >
                <Stack>
                  <Radio
                    size="xs"
                    value="text"
                    label={t('widget.setting.digital_human.default_input_mode_text')}
                    description={t('widget.setting.digital_human.default_input_mode_text_desc')}
                  />
                  <Radio
                    size="xs"
                    value="voice"
                    label={t('widget.setting.digital_human.default_input_mode_voice')}
                    description={t('widget.setting.digital_human.default_input_mode_voice_desc')}
                  />
                </Stack>
              </Radio.Group>
            </Stack>
          </Stack>
        </section>
      )}

      {/* 展示方式区域 */}
      <DisplayMethod showCustomize />
    </Stack>
  );
};

export default DigitalHumanSettings;
